import { EventV2, FullEvent } from '@front-app/services/EventsApi/EventModel';
import { Group, PreferenceToken } from '@front-app/services/GroupsApi/GroupModel';
import {
	ACTIVE_MEMBER_ROLES,
	FullUser,
	UserDetailRole,
	UserRole,
} from '@front-app/services/UserApi/UserModel';
import { toComputed } from './reactivity';

export function isCsr(user: FullUser | undefined): boolean {
	return user !== undefined && user.roles.includes(UserDetailRole.CSR);
}

function checkSingleToken(token: PreferenceToken): (group: Group | FullEvent | undefined) => boolean {
	return checkEveryMultipleToken([token]);
}

function checkEveryMultipleToken(tokens: PreferenceToken[]): (group: Group | FullEvent | undefined) => boolean {
	return (group: FullEvent | Group | undefined) => group !== undefined
		&& tokens.every((token) => group.preferenceTokens.includes(token));
}

function checkAnyMultipleToken(tokens: PreferenceToken[]): (group: Group | FullEvent | undefined) => boolean {
	return (group: FullEvent | Group | undefined) => group !== undefined
		&& tokens.some((token) => group.preferenceTokens.includes(token));
}

/**
 * Contents
 *
 * 1. Roles
 * 2. Group preference-based access
 * 3. Other
 * 4. Page-level Access
 * 5. Group Settings
 */

/* 1. Roles */
function roleEquals(roleA: UserRole): (roleB?: UserRole) => boolean {
	return (roleB?: UserRole): boolean => roleB === roleA;
}

/**
 * Returns true if the user has the administrator role in a group.
 *
 * This is used for cases where we care about a CSR's listed role NOT their privilege level. For
 * example, a status badge for "ADMIN" role only shows if the user's actual group role is set to
 * "admin", regardless of whether or not they are a CSR (otherwise CSRs would see all groups as "ADMIN"
 * which might be confusing).
 */
export const isAdminRole = roleEquals(UserRole.ADMINISTRATOR);
export const isModeratorRole = roleEquals(UserRole.MODERATOR);
export const isInviteeRole = roleEquals(UserRole.INVITEE);
export const isMemberRole = roleEquals(UserRole.MEMBER);

interface GroupRoledItem {
	groupRole: UserRole;
}
const compareUserRole = (fn: typeof isAdminRole) => (item: GroupRoledItem) => fn(item.groupRole);
export const userHasAdminRole = compareUserRole(isAdminRole);
export const userHasInviteeRole = compareUserRole(isInviteeRole);
export const userHasMemberRole = compareUserRole(isMemberRole);

export function compareEventRole(fn: (r: UserRole) => boolean): (e: EventV2) => boolean {
	return (event: EventV2) => fn(event.membershipRole);
}
export const eventHasAdminRole = compareEventRole(isAdminRole);
export const eventHasInviteeRole = compareEventRole(isInviteeRole);
export const eventHasMemberRole = compareEventRole(isMemberRole);

/**
 * For event management, we only care if the user is an admin on the event itself,
 * we no longer care about the parent groups.
 */
export const canUserManageEvent = (isUserCsr: boolean, event: FullEvent): boolean => {
	// If the user is a CSR, then it's OK
	if (isUserCsr) {
		return true;
	}
	// If the user is an admin AND is in the whitelist for beta testing, then it's ok
	return isAdminRole(event.role);
};

export function checkIfEventVisibleToUser(isUserCsr: boolean, event: FullEvent | undefined): boolean {
	if (event) {
		return canUserManageEvent(isUserCsr, event) || event.isPublished;
	}
	return false;
}

/* 2. Group preference-based access */
/**
 * Contents
 *
 * 2.1 Discussions
 * 2.2 Group join/leave
 * 2.3 Member list
 * 2.4 Files/Documents
 * 2.5 Categories
 * 2.6 Group Tab Access
 * 2.7 Data Dashboard
 * 2.8 Pinned Highlights
 */

/* 2.1 Discussions */

export const canViewDiscussions = checkSingleToken(PreferenceToken.DISCUSSION_VIEW);

/**
 * Determines whether or not to show anything at all about discussions. This should be permissive
 * as invitees are also allowed to see discussion content.
 *
 * @param group The group in question
 * @param isUserCsr Whether or not the current user is a CSR
 * @returns Boolean
 */
export function areDiscussionsVisibleForCurrentUser(group: Group, isUserCsr: boolean): boolean {
	if (group.preferenceTokens.includes(PreferenceToken.DISCUSSION_WRITE)) {
		return isUserCsr || ACTIVE_MEMBER_ROLES.includes(group.role) || group.role === UserRole.INVITEE;
	}
	if (group.preferenceTokens.includes(PreferenceToken.DISCUSSION_ADMIN_WRITE)) {
		return isUserCsr || group.role === UserRole.ADMINISTRATOR;
	}
	return false;
}

/**
 * Determines if the current user is allowed to see the CTA button to start
 * a discussion. If yes, they can attempt to start a discussion, but may be asked to go through the
 * "join" workflow.
 *
 * @param group The group in question
 * @param isUserCsr Whether or not the current user is a CSR
 * @returns Boolean
 */
export function isStartDiscussionCtaVisibleForCurrentUser(group: Group, isUserCsr: boolean): boolean {
	if (group.preferenceTokens.includes(PreferenceToken.DISCUSSION_WRITE)) {
		return isUserCsr || ACTIVE_MEMBER_ROLES.includes(group.role) || group.role === UserRole.INVITEE;
	}
	if (group.preferenceTokens.includes(PreferenceToken.DISCUSSION_ADMIN_WRITE)) {
		return isUserCsr || group.role === UserRole.ADMINISTRATOR;
	}
	return false;
}

export function canEditDiscussionSubject(group: Group, isUserCsr: boolean, isUserDiscussionAuthor: boolean): boolean {
	return canViewDiscussions(group)
		&& areDiscussionsStartableForCurrentUser(group, isUserCsr)
		&& (isUserCsr || isUserDiscussionAuthor);
}

/**
 * Determines if the current user is allowed to actually start a discussion in a group. ie: they
 * are not an invitee, but are a member.
 *
 * @param group The group in question
 * @param isUserCsr Whether or not the current user is a CSR
 * @returns Boolean
 */
export function areDiscussionsStartableForCurrentUser(group: Group, isUserCsr: boolean): boolean {
	if (group.preferenceTokens.includes(PreferenceToken.DISCUSSION_WRITE)) {
		return isUserCsr || ACTIVE_MEMBER_ROLES.includes(group.role);
	}
	if (group.preferenceTokens.includes(PreferenceToken.DISCUSSION_ADMIN_WRITE)) {
		return isUserCsr || group.role === UserRole.ADMINISTRATOR;
	}
	return false;
}

export const canUserReplyToDiscussion = areDiscussionsStartableForCurrentUser;

/* 2.2 Group join/leave */
export const shouldSeeLeaveWarning = checkSingleToken(PreferenceToken.LEAVE_LINK_WARN);
export const useUserMustAcceptGroupTerms = toComputed((group: Group | undefined): boolean => {
	if (isAdminRole(group?.role)) {
		return false;
	}
	return checkSingleToken(PreferenceToken.USER_TERMS_REQUIRED)(group);
});
/**
 * Event terms of use is only intended for _registrants_, NOT for people just casually
 * viewing the event.
 */
export const userMustAcceptEventTerms = (event: FullEvent | undefined, isUserCsr: boolean): boolean => {
	// CSRs should not have to accept any join requirements
	// Events without registration, cannot have TOU, as there is no registration happening
	if (isUserCsr || !event?.allowRegistration) {
		return false;
	}
	// Only members (not admins, invitees, departees) need to accept TOU
	if (isMemberRole(event?.role)) {
		return checkSingleToken(PreferenceToken.USER_TERMS_REQUIRED)(event);
	}
	return false;
};
export const userShouldAutoAcceptTerms = (event: FullEvent | undefined, isUserCsr: boolean): boolean => {
	// If join is disabled, then it shouldn't be possible to accept terms
	if (!event?.allowRegistration) {
		return false;
	}
	// Admins should auto-accept TOU
	if (isAdminRole(event?.role) || (ACTIVE_MEMBER_ROLES.includes(event?.role) && isUserCsr)) {
		return checkSingleToken(PreferenceToken.USER_TERMS_REQUIRED)(event);
	}
	return false;
};
export const userMustSharePersonalInformation = (group: Group | undefined, isUserCsr: boolean): boolean => {
	if (isUserCsr) {
		return false;
	}
	return checkSingleToken(PreferenceToken.SHARE_PERSONAL_INFORMATION_REQUIRED)(group);
};
export const userMustEnterAccessCode = (group: Group | undefined, isUserCsr: boolean): boolean => {
	if (isUserCsr) {
		return false;
	}
	return checkSingleToken(PreferenceToken.ACCESS_CODE_REQUIRED)(group);
};
export const showLimitedAccessMessage = checkSingleToken(PreferenceToken.LIMITED_ACCESS_MESSAGE);
export const professionalRoleRequired = checkSingleToken(PreferenceToken.PROFESSIONAL_ROLE_REQUIRED);
export const professionalRoleVerificationRequired = checkSingleToken(PreferenceToken.PROFESSIONAL_ROLE_VERIFICATION_REQUIRED);
export const showProfessionalRoleRequiredPage = checkAnyMultipleToken([
	PreferenceToken.PROFESSIONAL_ROLE_REQUIRED,
	PreferenceToken.PROFESSIONAL_ROLE_VERIFICATION_REQUIRED,
]);

export function canLeave(group: Group | FullEvent): boolean {
	return group.preferenceTokens.includes(PreferenceToken.LEAVE_LINK)
		|| group.preferenceTokens.includes(PreferenceToken.LEAVE_LINK_WARN);
}

export const canSeeLimitedPreview = (group: Group): boolean => {
	return checkSingleToken(PreferenceToken.PRIVATE_GROUP_PREVIEW)(group);
};

export function canSeeJoinLink(group: Group): boolean {
	// Currently join links are only within the group, where the user will see the TOU
	// already, so allowing the link at the same time as the TOU button would be confusing
	return group.preferenceTokens.includes(PreferenceToken.JOIN_LINK)
		&& !group.preferenceTokens.includes(PreferenceToken.USER_TERMS_REQUIRED)
		&& !showProfessionalRoleRequiredPage(group);
}

export function hasGroupAccess(group: Group | undefined, isUserCsr: boolean): boolean {
	if (!group) {
		return false;
	}
	return isUserCsr
		|| ACTIVE_MEMBER_ROLES.includes(group.role)
		|| isInviteeRole(group.role)
		// OPEN and MANAGED groups allow DEPARTEE and non-invitee users to have access, so we check if a non-member can join/terms
		// to handle that case
		|| group.preferenceTokens.includes(PreferenceToken.JOIN_LINK)
		|| group.preferenceTokens.includes(PreferenceToken.USER_TERMS_REQUIRED);
}

/* 2.3 Member list */
export const useCanSeeMemberWidget = toComputed(checkSingleToken(PreferenceToken.MEMBER_WIDGET_VIEW));

export const canSeeMemberList = checkAnyMultipleToken([PreferenceToken.MEMBER_LIST_VIEW, PreferenceToken.ADMIN_PAGE_VIEW]);
export const canSeeMonitorList = checkAnyMultipleToken([PreferenceToken.MONITOR_LIST_VIEW, PreferenceToken.ADMIN_PAGE_VIEW]);
export const canSeeAdminList = checkAnyMultipleToken([PreferenceToken.ADMIN_LIST_VIEW, PreferenceToken.ADMIN_PAGE_VIEW]);
export const canSeeInviteeList = checkAnyMultipleToken([PreferenceToken.INVITEE_LIST_VIEW, PreferenceToken.ADMIN_PAGE_VIEW]);
export const canSeeDeparteeList = checkAnyMultipleToken([PreferenceToken.DEPARTEE_LIST_VIEW, PreferenceToken.ADMIN_PAGE_VIEW]);
export const canSeeExternalInviteeList = checkAnyMultipleToken([PreferenceToken.EXTERNAL_INVITEE_LIST_VIEW, PreferenceToken.ADMIN_PAGE_VIEW]);
export const canSeeAtLeastOneRestrictedUserList = checkAnyMultipleToken([
	PreferenceToken.ADMIN_PAGE_VIEW,
	PreferenceToken.INVITEE_LIST_VIEW,
	PreferenceToken.DEPARTEE_LIST_VIEW,
	PreferenceToken.EXTERNAL_INVITEE_LIST_VIEW,
]);
export const canSeeAtLeastOneMemberList = checkAnyMultipleToken([
	PreferenceToken.ADMIN_PAGE_VIEW,
	PreferenceToken.MEMBER_LIST_VIEW,
	PreferenceToken.MONITOR_LIST_VIEW,
	PreferenceToken.ADMIN_LIST_VIEW,
	PreferenceToken.INVITEE_LIST_VIEW,
	PreferenceToken.DEPARTEE_LIST_VIEW,
	PreferenceToken.EXTERNAL_INVITEE_LIST_VIEW,
]);

export const canManageMembers = checkSingleToken(PreferenceToken.MANAGE_MEMBERS);

export const canInviteMembers = checkSingleToken(PreferenceToken.INVITE_MEMBERS);
export const canInviteMonitors = checkSingleToken(PreferenceToken.INVITE_MONITORS);
export const canInviteAdmins = checkSingleToken(PreferenceToken.INVITE_ADMINISTRATORS);
export const canInviteExternal = checkSingleToken(PreferenceToken.EXTERNAL_INVITE_MEMBERS);
export const canInviteExternalBulk = checkSingleToken(PreferenceToken.EXTERNAL_BULK_INVITE_MEMBERS);
export const canInviteAtLeastOneMemberType = checkAnyMultipleToken([
	PreferenceToken.INVITE_MEMBERS,
	PreferenceToken.INVITE_MONITORS,
	PreferenceToken.INVITE_ADMINISTRATORS,
	PreferenceToken.EXTERNAL_INVITE_MEMBERS,
	PreferenceToken.EXTERNAL_BULK_INVITE_MEMBERS,
]);

/* 2.4 Files/Documents */
export const isFileSharingEnabled = checkSingleToken(PreferenceToken.UPLOAD_FILE);
export const isFileUpdatingEnabled = checkSingleToken(PreferenceToken.REMOVE_FILE);
export const canViewFileList = checkSingleToken(PreferenceToken.FILE_LIST_VIEW);
export const canShareFilesToGroup = (group: Group | FullEvent | undefined): boolean => isFileSharingEnabled(group) && canViewFileList(group);
export const canViewFile = checkSingleToken(PreferenceToken.FILE_VIEW);
export const canEditOrDeleteGroupFiles = (group: Group | FullEvent | undefined): boolean => isFileUpdatingEnabled(group) && canViewFileList(group);

/* 2.5 Categories */
export const useCanSeeCategoryWidget = toComputed(checkSingleToken(PreferenceToken.CATEGORY_WIDGET));
export const canManageDocumentCategories = checkSingleToken(PreferenceToken.MANAGE_DOCUMENT_CATEGORY);
export const canManageDiscussionCategories = checkSingleToken(PreferenceToken.MANAGE_DISCUSSION_CATEGORY);
export function canEditDiscussionCategories(group: Group, isUserCsr: boolean, isUserDiscussionAuthor: boolean): boolean {
	return canViewDiscussions(group)
		&& canManageDiscussionCategories(group)
		&& areDiscussionsStartableForCurrentUser(group, isUserCsr)
		&& (isUserCsr || isUserDiscussionAuthor);
}

/* 2.6 Group Tab Access */
export const canViewMainTab = checkSingleToken(PreferenceToken.HEADLINE_VIEW);
export const canViewGroupEvents = checkSingleToken(PreferenceToken.VIEW_EVENT);
export const canViewContentTab = (group: Group | undefined): boolean => canViewDiscussions(group)
	|| canViewFileList(group);
export const canViewCustomTab = checkSingleToken(PreferenceToken.CUSTOM_PAGE);
const isLmsEnabled = checkSingleToken(PreferenceToken.ENABLE_LMS);
export const canViewLmsTab = isLmsEnabled;

/* 2.6 Data Dashboard */
export const canViewEnhancedDashboard = checkSingleToken(PreferenceToken.ENHANCED_DATA_DASHBOARDS);

/* 2.7 Group Events */
const checkCreateEventToken = checkSingleToken(PreferenceToken.CREATE_EVENT);
export const canCreateGroupEvents = (isUserCsr: boolean, group: Group | FullEvent | undefined): boolean => {
	if (!canViewGroupEvents(group)) {
		return false;
	}
	if (isUserCsr) {
		return true;
	}
	return checkCreateEventToken(group);
};
const checkEditEventsToken = checkSingleToken(PreferenceToken.EDIT_EVENT);
export const canModifyGroupEvents = (isUserCsr: boolean, group: Group | FullEvent | undefined): boolean => {
	if (isUserCsr) {
		return true;
	}
	return checkEditEventsToken(group);
};

/* 2.8 Pinned Highlights */
export const canViewPinnedHighlights = checkSingleToken(PreferenceToken.VIEW_HIGHLIGHTS);

/* 3. Other */

/**
 * Returns true if the user is allowed to perform administrative functions in a group
 *
 * @param group The group under consideration
 * @param isUserCsr True if the user under consideration is a CSR
 */
export function hasAdminPrivileges(role: UserRole | undefined, isUserCsr: boolean): boolean {
	return isUserCsr || isAdminRole(role);
}

/* 4. Page-level Access */
export const canViewGroupAdminPage = (group: Group, isUserCsr: boolean): boolean => isUserCsr
	|| canViewGroupHighlightsAdminPage(group, isUserCsr)
	|| canViewDataDashboardPage([group], isUserCsr);

const canViewAdminSettings = checkSingleToken(PreferenceToken.ADMIN_PAGE_VIEW);
const canEditCustomPage = checkSingleToken(PreferenceToken.EDIT_CUSTOM_PAGE);

export const canViewGroupNotificationSettingsPage = checkSingleToken(PreferenceToken.EDIT_NOTIFICATION);

export function canViewGroupDigestManagementPage(group: Group | undefined, isUserCsr: boolean): boolean {
	return isUserCsr || isAdminRole(group?.role);
}

export function canViewGroupHighlightsAdminPage(group: Group | undefined, isUserCsr: boolean): boolean {
	return isUserCsr || isAdminRole(group?.role);
}

export function canViewGroupAdminCustomPage(group: Group | undefined, isUserCsr: boolean): boolean {
	if (isUserCsr) {
		return true;
	}
	return isAdminRole(group?.role) && canEditCustomPage(group);
}

export function canViewMemberManagementPage(group: Group | undefined, isUserCsr: boolean): boolean {
	if (isUserCsr) {
		return true;
	}
	return isAdminRole(group?.role) && (canSeeAtLeastOneMemberList(group) || canInviteAtLeastOneMemberType(group));
}

export function canViewDataDashboardPage(groups: Group[], isUserCsr: boolean): boolean {
	if (isUserCsr) {
		return true;
	}

	return groups.some(({ role }) => isAdminRole(role));
}

export function canViewDataDashboardPageForGroup(group: Group | undefined, isUserCsr: boolean): boolean {
	if (!group) {
		return false;
	}
	return canViewDataDashboardPage([group], isUserCsr);
}

// User can access the LMS page if they can view at least one of the sub-pages
export function canViewLearningProgramsAdminPage(group: Group | undefined, isUserCsr: boolean): boolean {
	return canViewLearningProgramsSurveyResultsPage(group, isUserCsr)
		|| canViewLearningProgramsManagementPage(group, isUserCsr);
}

export function canViewLearningProgramsManagementPage(group: Group | undefined, isUserCsr: boolean): boolean {
	return isUserCsr && isLmsEnabled(group);
}

export function canViewLearningProgramsSurveyResultsPage(group: Group | undefined, isUserCsr: boolean): boolean {
	return hasAdminPrivileges(group?.role, isUserCsr) && isLmsEnabled(group);
}

export const canManageCategories = checkSingleToken(PreferenceToken.MANAGE_GROUP_CATEGORY);

export function canViewGroupConfigurationPage(group: Group | undefined, isUserCsr: boolean): boolean {
	return isUserCsr || canManageCategories(group);
}

export function canViewGroupSettingsPage(group: Group | undefined, isUserCsr: boolean): boolean {
	return isUserCsr || canViewAdminSettings(group);
}

export function canViewGroupOtherSettingsPage(group: Group | undefined, isUserCsr: boolean): boolean {
	return isUserCsr || canViewAdminSettings(group);
}

export function canViewInviteCampaignTab(group: Group | undefined, isUserCsr: boolean): boolean {
	if (isUserCsr) {
		return true;
	}
	return canInviteMembers(group) && canInviteExternalBulk(group);
}

/* 5. Group Settings */
export const areModeratorNotificationsAvailable = checkAnyMultipleToken([
	PreferenceToken.DISCUSSION_MODERATOR_NOTIFY,
	PreferenceToken.FILE_MODERATOR_NOTIFY,
]);

export const areModeratorDiscussionNotificationsAvailable = checkSingleToken(PreferenceToken.DISCUSSION_MODERATOR_NOTIFY);
export const areModeratorFileNotificationsAvailable = checkSingleToken(PreferenceToken.FILE_MODERATOR_NOTIFY);
